<script setup lang="ts">
import { computed } from 'vue';

import { TokenizedPaymentMethod } from '@/api/paymentMethod';
import CreditCardLogo from '@/components/base/assets/CreditCardLogo.vue';
import RadioPill from '@/components/base/form/RadioPill.vue';

const props = defineProps<{
  id: string;
  modelValue?: TokenizedPaymentMethod;
  paymentMethod: TokenizedPaymentMethod;
  variant?: 'declined' | 'success';
}>();

const emit = defineEmits(['update:modelValue']);

const selectedPaymentMethod = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
});

const isSelected = computed(() => props.paymentMethod.nonce === selectedPaymentMethod.value?.nonce);

const borderColorUtils = computed(() => {
  if (props.variant === 'success') return 'border-nuts-lime-800';
  if (isSelected.value) return 'border-black';
  return 'border-neutral-200';
});

const paymentType = computed(() => props.paymentMethod?.details?.cardType);
const isApplePay = computed(() => paymentType.value === 'Apple Pay');
const isPaypal = computed(() => paymentType.value === 'PayPal');
const paypalEmail = computed(() => {
  if (!isPaypal.value) return '';
  // @ts-expect-error TS thinks external_account_token does not exist but it exists when payment is paypal
  const email = props.paymentMethod.external_account_token ?? '';
  const threshold = props.variant === 'declined' ? 20 : 28;
  return email.length > threshold
    ? `${email.split('@')[0].substring(0, 4)}...@${email.split('@')[1]}`
    : email;
});
</script>

<template>
  <RadioPill
    v-model="selectedPaymentMethod"
    aria-label="Payment method selector"
    class="px-4 py-3 md:px-8 md:py-4"
    :class="borderColorUtils"
    data-test="payment-method-selector"
    name="payment-method-type"
    :value="paymentMethod"
  >
    <div class="flex items-center w-full font-normal leading-6 md:leading-7">
      <span
        class="mr-2"
        :class="{ 'hidden sm:visible sm:block': isPaypal && paypalEmail.length >= 26 }"
      >
        {{ paymentMethod.description }}
      </span>
      <template v-if="isPaypal">{{ paypalEmail }}</template>
      <span v-if="variant === 'declined'" class="ml-2 font-semibold text-nuts-red-800"
        >Declined</span
      >
      <svg
        v-else-if="variant === 'success'"
        class="ml-2"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M10 19V19C5.029 19 1 14.971 1 10V10C1 5.029 5.029 1 10 1V1C14.971 1 19 5.029 19 10V10C19 14.971 14.971 19 10 19Z"
          stroke="#A0CF67"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M14 8L9 13L6 10"
          stroke="#A0CF67"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
    <img
      v-if="isApplePay"
      class="object-contain w-6 h-6"
      src="@/assets/apple-pay-mark.svg"
      alt="Apple Pay logo"
    />
    <img
      v-else-if="isPaypal"
      class="object-contain w-6 h-6"
      src="@/assets/paypal-logo-small.svg"
      alt="PayPal logo"
    />
    <CreditCardLogo v-else class="object-contain w-6 h-6" :cardType="paymentType" />
  </RadioPill>
</template>
